import { ICONS } from '../ui/icons';
import {
  DOWS, advanceFor, balanceDue, calc, dateOf, dayOfWeek, fDate, fShort, fTime, firstName, inr, span, toInstant,
  type BookingRecord, type Customer, type Item, type Settings,
} from '@nandanam/domain';

/**
 * The quotation as a picture for WhatsApp, drawn on a canvas from the booking so the numbers are always
 * the app's own. Layout follows the design handoff (QUOTATION_SPEC.md, v2): a 540 px column drawn at 2× → 1080 px PNG.
 */

const W = 540, SCALE = 2, PADX = 22;
const T = {
  slate: '#2F4F4F', beige: '#F5F5DC', sky: '#87CEEB', lightBlue: '#ADD8E6', page: '#f8f1e6', card: '#fffdf9',
  ink: '#1d2a2a', body: '#4e5d5d', muted: '#6b7a7a', faint: '#7a8888', divider: '#f0ebe1', red: '#B3261E',
};
const HEAD = "'Marcellus', Georgia, serif", BODY = "'Nunito Sans', system-ui, sans-serif";
const PHOTO: Record<string, string> = { evA: 'gazebo', evB: 'pool', stR: 'bedroom-carved', stH: 'courtyard' };

type Line = { label: string; value: string; red?: boolean };
type Step = { title: string; sub: string; value: string };
export interface QuoteData {
  ref: string; greeting: string; intro: string; isStay: boolean;
  occasion: string; guests: string; date: string; time: string;
  pkgName: string; pkgDesc: string; pkgPhoto: string;
  lines: Line[]; total: string; totalNote: string; steps: Step[];
  deposit: { value: string; sub: string } | null;
  closing: string; question: string; team: string; city: string; phone: string; name: string; tagline: string;
}

const days = (i: Item) => Math.max(1, Math.round((toInstant(i.end) - toInstant(i.start)) / 86_400_000));
const dateLong = (s: string) => `${DOWS[dayOfWeek(dateOf(s))]}, ${fDate(s, { dow: false, year: true })}`;

/** "Lawns, pool & outdoor areas, and one bedroom" */
function areaText(areas: string[]): string {
  const a = areas.map((x, i) => (i ? x.charAt(0).toLowerCase() + x.slice(1) : x));
  return a.length > 1 ? `${a.slice(0, -1).join(', ')}, and ${a[a.length - 1]}` : (a[0] ?? '');
}

export function quoteData(r: BookingRecord, c: Customer, s: Settings, number: string, today: string): QuoteData {
  const k = calc(r), sp = span(r), P = s.property, tba = 'To be agreed', stay = r.kind === 'stay';
  const sameDay = dateOf(sp.start) === dateOf(sp.end);
  const lead = r.items.find(i => i.kind === 'event') ?? r.items[0]!;
  const pkg = s.packages.find(p => p.id === lead.pkg);
  const lines: Line[] = r.items.map(i => ({
    label: `${i.pkgName} · ${i.kind === 'stay' ? `${days(i)} × 24 h` : `${days(i)} day${days(i) > 1 ? 's' : ''}`}`,
    value: i.custom && i.agreed <= 0 ? tba : inr(i.agreed),
  }));
  if (k.extra) lines.push({ label: 'Extra guests', value: inr(k.extra) });
  if (k.clean) lines.push({ label: 'Cleaning', value: inr(k.clean) });
  if (k.disc) lines.push({ label: `Discount${r.discount?.reason ? ` · ${r.discount.reason}` : ''}`, value: `−${inr(k.disc)}`, red: true });
  const adv = advanceFor(k.total, r.terms), dd = r.terms.balanceDueDays;
  const steps: Step[] = [
    { title: r.terms.advanceMode === 'percent' ? `Booking advance · ${r.terms.advancePct}%` : 'Booking advance', sub: 'Confirms your date', value: k.pending ? tba : inr(adv) },
    { title: 'Remaining balance', sub: dd > 0 ? `By ${DOWS[dayOfWeek(balanceDue(r))]}, ${fDate(balanceDue(r), { dow: false })} — ${dd === 1 ? 'one day' : `${dd} days`} before` : 'On the day', value: k.pending ? tba : inr(Math.max(0, k.total - adv)) },
  ];
  return {
    ref: `${number} · ${fDate(today, { dow: false, year: true })}`,
    greeting: `Namaste ${firstName(c)} Garu,`, isStay: stay,
    intro: `Thank you for choosing ${P.name}. Here is the quotation for your ${stay ? 'stay' : 'celebration'}.`,
    occasion: stay ? 'Stay' : r.type, guests: String(r.guests),
    date: sameDay ? dateLong(sp.start) : `${fShort(sp.start, sp.end)} ${sp.end.slice(0, 4)}`,
    time: sameDay ? `${fTime(sp.start)} – ${fTime(sp.end)}` : `In ${fTime(sp.start)} · Out ${fTime(sp.end)}`,
    pkgName: r.items.map(i => i.pkgName).join(' + '),
    pkgDesc: `${areaText(pkg?.areas ?? [])}${lead.kind === 'event' ? ' · whole property exclusive to you' : ''}`,
    pkgPhoto: `assets/${PHOTO[lead.pkg] ?? 'gazebo'}.jpg`,
    lines, total: k.pending ? tba : inr(k.total), totalNote: s.taxNote || 'GST not applicable', steps,
    deposit: r.deposit.required > 0 ? { value: inr(r.deposit.required), sub: `Paid on arrival · refunded within ${r.terms.refundWindow} · not in the total` } : null,
    closing: stay ? `We look forward to welcoming you for a relaxing stay at ${P.name}.` : `We look forward to welcoming you and your guests for a wonderful celebration at ${P.name}.`,
    question: 'Shall we hold the date for you?', team: `Team ${P.name}`, city: P.city, phone: P.phone, name: P.name,
    tagline: 'Your perfect venue for memorable celebrations',
  };
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise(res => { const i = new Image(); i.onload = () => res(i); i.onerror = () => res(null); i.src = src; });
}

/** A 540-wide canvas drawn at 2×, with the drawing helpers every picture shares. */
function painter(logo: HTMLImageElement | null) {
  const cv = document.createElement('canvas'); cv.width = W * SCALE; cv.height = 2600 * SCALE;
  const x = cv.getContext('2d')!;
  x.scale(SCALE, SCALE);
  x.fillStyle = T.page; x.fillRect(0, 0, W, 2600);

  const font = (size: number, weight = 400, head = false) => { x.font = `${head ? '' : weight + ' '}${size}px ${head ? HEAD : BODY}`; };
  // letterSpacing is newer than the rest of canvas; where it is missing the text is simply unspaced.
  const spacing = (px: number) => { if ('letterSpacing' in x) (x as unknown as { letterSpacing: string }).letterSpacing = `${px}px`; };
  /** Draw one line of text in a line box of height lh starting at top. */
  const text = (s: string, l: number, top: number, lh: number, color: string, align: CanvasTextAlign = 'left') => {
    x.fillStyle = color; x.textAlign = align; x.textBaseline = 'middle'; x.fillText(s, l, top + lh / 2); x.textAlign = 'left';
  };
  const wrap = (s: string, max: number) => {
    const out: string[] = []; let line = '';
    for (const w of s.split(' ')) { const t = line ? `${line} ${w}` : w; if (x.measureText(t).width > max && line) { out.push(line); line = w; } else line = t; }
    if (line) out.push(line); return out;
  };
  const box = (l: number, t: number, w: number, h: number, r: number) => { x.beginPath(); x.roundRect(l, t, w, h, r); };
  const clipTo = (l: number, t: number, w: number, h: number) => { x.save(); x.beginPath(); x.rect(l, t, w, h); x.clip(); };
  const cover = (img: HTMLImageElement | null, l: number, t: number, w: number, h: number, r: number) => {
    x.save(); box(l, t, w, h, r); x.clip();
    if (img) { const k = Math.max(w / img.width, h / img.height); x.drawImage(img, l + (w - img.width * k) / 2, t + (h - img.height * k) / 2, img.width * k, img.height * k); }
    else { x.fillStyle = T.slate; x.fillRect(l, t, w, h); }
    x.restore();
  };
  const icon = (name: string, cx: number, cy: number, size: number, color: string) => {
    const k = size / 24; x.save(); x.translate(cx - size / 2, cy - size / 2); x.scale(k, k);
    x.strokeStyle = color; x.lineWidth = 1.8; x.lineCap = 'round'; x.lineJoin = 'round';
    for (const p of ICONS[name] ?? []) x.stroke(new Path2D(p));
    x.restore();
  };
  const card = (t: number, h: number) => {
    x.save(); x.shadowColor = 'rgba(90,70,40,0.07)'; x.shadowBlur = 22; x.shadowOffsetY = 8;
    x.fillStyle = T.card; box(PADX, t, W - 2 * PADX, h, 20); x.fill(); x.restore();
  };
  const pill = (s: string, right: number, top: number, padX: number, padY: number, size: number, bg: string, fg: string) => {
    const w = x.measureText(s).width + 2 * padX, h = size * 1.3 + 2 * padY;
    x.fillStyle = bg; box(right - w, top, w, h, h / 2); x.fill(); text(s, right - w / 2, top, h, fg, 'center'); return h;
  };
  const header = (l: number, t: number, ic: string, title: string) => {
    x.fillStyle = T.slate; box(l, t, 34, 34, 11); x.fill(); icon(ic, l + 17, t + 17, 17, T.beige);
    font(20, 400, true); text(title, l + 44, t, 34, T.ink);
  };


  /** Logo-led header: label pill and reference above the sketch logo, tagline below. Returns its height. */
  const brand = (label: string, ref: string, tagline: string, LG = 150) => {
    // A smaller logo also tightens the spacing, for pictures that should fit one phone screen.
    const small = LG < 120, TOP = small ? 16 : 30, G1 = small ? 10 : 12, G2 = small ? 8 : 12, BOT = small ? 14 : 22;
    const pillH = 11 * 1.3 + 10, HH = TOP + pillH + G1 + LG + G2 + 14 + BOT;
    const g = x.createLinearGradient(0, 0, 0, HH);
    g.addColorStop(0, '#fffdf9'); g.addColorStop(1, '#f8f1e6');
    x.fillStyle = g; x.fillRect(0, 0, W, HH);
    x.fillStyle = '#ece3d4'; x.fillRect(0, HH - 1, W, 1);
    font(11, 700); spacing(11 * 0.1); const qw = x.measureText(label).width + 24; spacing(0);
    font(11.5, 600); const rw = x.measureText(ref).width + 22;
    const pr = (W - (qw + 8 + rw)) / 2 + qw;
    font(11, 700); spacing(11 * 0.1); pill(label, pr, TOP, 12, 5, 11, T.slate, T.beige); spacing(0);
    font(11.5, 600); pill(ref, pr + 8 + rw, TOP + (pillH - (11.5 * 1.3 + 8)) / 2, 11, 4, 11.5, '#e1ebe9', T.slate);
    const lcx = W / 2, lcy = TOP + pillH + G1 + LG / 2;
    x.save(); x.shadowColor = 'rgba(90,70,40,0.16)'; x.shadowBlur = 28; x.shadowOffsetY = 12;
    x.fillStyle = '#e3dccf'; x.beginPath(); x.arc(lcx, lcy, LG / 2 + 5, 0, Math.PI * 2); x.fill(); x.restore();
    x.fillStyle = '#fff'; x.beginPath(); x.arc(lcx, lcy, LG / 2 + 4, 0, Math.PI * 2); x.fill();
    x.save(); x.beginPath(); x.arc(lcx, lcy, LG / 2, 0, Math.PI * 2); x.clip();
    if (logo) x.drawImage(logo, lcx - LG / 2, lcy - LG / 2, LG, LG);
    x.restore();
    font(10.5, 700); spacing(10.5 * 0.16); text(tagline.toUpperCase(), W / 2, lcy + LG / 2 + G2, 14, '#4a7370', 'center'); spacing(0);
    return HH;
  };

  /** Warm regards, team, city and the phone pill. Returns the bottom of the picture. */
  const footer = (y: number, team: string, city: string, phone: string, compact = false) => {
    const l = PADX + 6, top = y + (compact ? 2 : 6), fh = compact ? 24 + 2 + 18 : 18 + 2 + 24 + 2 + 18;
    if (compact) {
      font(18, 400, true); text(team, l, top, 24, T.ink);
      font(12.5); text(city, l, top + 26, 18, T.muted);
    } else {
      font(12.5); text('Warm regards,', l, top, 18, T.faint);
      font(19, 400, true); text(team, l, top + 20, 24, T.ink);
      font(12.5); text(city, l, top + 46, 18, T.muted);
    }
    font(14, 700); const pw = 14 + 15 + 8 + x.measureText(phone).width + 14, phh = 38, pr = W - PADX - 6;
    x.fillStyle = T.slate; box(pr - pw, top + fh - phh, pw, phh, phh / 2); x.fill();
    icon('phone', pr - pw + 14 + 7.5, top + fh - phh / 2, 15, T.beige);
    text(phone, pr - pw + 14 + 15 + 8, top + fh - phh, phh, T.beige);
    return top + fh + (compact ? 18 : 26);
  };

  const finish = (bottom: number) => {
    const out = document.createElement('canvas'); out.width = W * SCALE; out.height = Math.ceil(bottom * SCALE);
    out.getContext('2d')!.drawImage(cv, 0, 0);
    return out;
  };
  return { cv, x, font, spacing, text, wrap, box, clipTo, cover, icon, card, pill, header, brand, footer, finish };
}

export async function drawQuote(d: QuoteData): Promise<HTMLCanvasElement> {
  await Promise.all([`20px ${HEAD}`, `400 14px ${BODY}`, `600 14px ${BODY}`, `700 14px ${BODY}`].map(f => document.fonts.load(f).catch(() => null)));
  const [logo, thumb] = await Promise.all([loadImage('assets/logo-sketch.png'), loadImage(d.pkgPhoto)]);
  const p = painter(logo);
  const { x, font, text, wrap, box, clipTo, cover, icon, card, header } = p;
  let y = p.brand('QUOTATION', d.ref, d.tagline, 84) + 16;
  const CL = PADX + 18, CW = W - 2 * PADX - 36; // content box inside the cards
  const GAP = 10, PT = 14; // kept tight so the whole quotation fits one phone screen

  /* Greeting */
  {
    font(24, 400, true); text(d.greeting, PADX + 6, y + 4, 29, T.ink);
    font(14.5); const ls = wrap(d.intro, W - 2 * PADX - 12);
    ls.forEach((s, i) => text(s, PADX + 6, y + 4 + 29 + 8 + i * 23.2, 23.2, T.body));
    y += 4 + 29 + 8 + ls.length * 23.2 + 2 + GAP;
  }

  /* Event details */
  {
    font(12.5); const desc = wrap(d.pkgDesc, CW - 72);
    const tileH = 52, pkgH = Math.max(48, 21 + desc.length * 18);
    const h = PT + 34 + 10 + tileH * 2 + 8 + 10 + 1 + 10 + pkgH + 10 + 4;
    card(y, h); let t = y + PT;
    header(CL, t, 'calendar', d.isStay ? 'Stay details' : 'Event details'); t += 34 + 10;
    const tw2 = (CW - 8) / 2;
    const tiles: [string, string, string, string, string][] = [
      ['Occasion', d.occasion, '#dff1f9', '#26708f', '#1d4a5e'], ['Guests', d.guests, '#e1ebe9', '#4a7370', '#1b2f2f'],
      ['Date', d.date, '#f3efe8', T.muted, T.ink], ['Time', d.time, '#f3efe8', T.muted, T.ink],
    ];
    tiles.forEach(([lab, val, bg, lc, vc], i) => {
      const l = CL + (i % 2) * (tw2 + 8), tt = t + Math.floor(i / 2) * (tileH + 8);
      x.fillStyle = bg; box(l, tt, tw2, tileH, 14); x.fill();
      font(11.5); text(lab, l + 12, tt + 8, 16, lc);
      font(15, 700); clipTo(l, tt, tw2 - 8, tileH); text(val, l + 12, tt + 8 + 16 + 2, 20, vc); x.restore();
    });
    t += tileH * 2 + 8 + 10;
    x.fillStyle = T.divider; x.fillRect(CL, t, CW, 1); t += 1 + 10;
    cover(thumb, CL, t + (pkgH - 48) / 2, 60, 48, 11);
    font(15, 700); text(d.pkgName, CL + 72, t, 21, T.ink);
    font(12.5); desc.forEach((s, i) => text(s, CL + 72, t + 21 + i * 18, 18, T.muted));
    y += h + GAP;
  }

  /* Quotation */
  {
    const rowH = 38, bandH = 60;
    const h = PT + 34 + 4 + d.lines.length * rowH + 6 + bandH + PT;
    card(y, h); let t = y + PT;
    header(CL, t, 'rupee', 'Quotation'); t += 34 + 4;
    d.lines.forEach((ln, i) => {
      font(14.5); clipTo(CL, t, CW - 110, rowH); text(ln.label, CL + 2, t, rowH, T.ink); x.restore();
      font(14.5, 700); text(ln.value, CL + CW - 2, t, rowH, ln.red ? T.red : T.ink, 'right');
      if (i < d.lines.length - 1) { x.fillStyle = T.divider; x.fillRect(CL, t + rowH - 1, CW, 1); }
      t += rowH;
    });
    t += 6;
    x.fillStyle = T.slate; box(CL, t, CW, bandH, 16); x.fill();
    font(20, 400, true); text('Total package', CL + 16, t + 10, 23, T.beige);
    font(11.5); text(d.totalNote, CL + 16, t + 10 + 23 + 2, 15, T.lightBlue);
    font(d.total.length > 10 ? 22 : 30, 400, true); text(d.total, CL + CW - 16, t, bandH, T.beige, 'right');
    y += h + 14;
  }

  /* How to pay */
  {
    const rowH = 48;
    font(16, 700); const amtW = d.deposit ? x.measureText(d.deposit.value).width : 0;
    font(12); const dsub = d.deposit ? wrap(d.deposit.sub, CW - 28 - 20 - 12 - amtW - 12) : [];
    const depH = 10 + 20 + dsub.length * 17.4 + 10;
    const h = PT + 34 + 6 + rowH * 2 + 6 + (d.deposit ? 8 + depH : 0) + PT;
    card(y, h); let t = y + PT;
    header(CL, t, 'clock', 'How to pay'); t += 34 + 6;
    d.steps.forEach((st, i) => {
      x.fillStyle = i ? '#e1ebe9' : T.sky; x.beginPath(); x.arc(CL + 14, t + rowH / 2, 14, 0, Math.PI * 2); x.fill();
      font(13, 700); text(String(i + 1), CL + 14, t, rowH, i ? T.slate : '#1b2f2f', 'center');
      font(17, 700); const aw = x.measureText(st.value).width; text(st.value, CL + CW, t, rowH, T.ink, 'right');
      clipTo(CL + 42, t, CW - 42 - aw - 12, rowH);
      font(15, 700); text(st.title, CL + 42, t + 4, 22, T.ink);
      font(12.5); text(st.sub, CL + 42, t + 4 + 22, 18, T.muted); x.restore();
      t += rowH;
      if (!i) { x.fillStyle = '#d3e2df'; x.fillRect(CL + 13, t - 6, 2, 12); t += 6; }
    });
    if (d.deposit) {
      t += 8;
      x.fillStyle = '#f4f8f7'; box(CL, t, CW, depH, 14); x.fill();
      x.save(); x.setLineDash([5, 4]); x.strokeStyle = '#9fbab5'; x.lineWidth = 1.5; box(CL + 0.75, t + 0.75, CW - 1.5, depH - 1.5, 14); x.stroke(); x.restore();
      icon('shield', CL + 14 + 10, t + depH / 2, 20, '#4a7370');
      const dl = CL + 14 + 20 + 12;
      font(14, 700); text('Refundable caution deposit', dl, t + 10, 20, T.ink);
      font(12); dsub.forEach((s, i) => text(s, dl, t + 10 + 20 + i * 17.4, 17.4, '#5f6e6e'));
      font(16, 700); text(d.deposit.value, CL + CW - 14, t, depH, T.ink, 'right');
    }
    y += h + GAP;
  }

  /* Closing: just the question, to keep the picture to one screen. */
  {
    font(19, 400, true); const q = wrap(d.question, W - 2 * PADX - 40);
    const h = 13 + q.length * 25 + 13;
    x.fillStyle = T.beige; box(PADX, y, W - 2 * PADX, h, 18); x.fill();
    q.forEach((s, i) => text(s, W / 2, y + 13 + i * 25, 25, T.slate, 'center'));
    y += h + GAP;
  }

  return p.finish(p.footer(y, d.team, d.city, d.phone, true));
}

/* ---------- Every other WhatsApp template, as a picture in the same style ---------- */

type Block = { kind: 'text'; lines: string[] } | { kind: 'kv'; rows: [string, string][] } | { kind: 'bullets'; items: string[] };
export interface MessageData { label: string; key: string; ref: string; greeting: string; intro: string; blocks: Block[]; team: string; city: string; phone: string; tagline: string }

const KV = /^([^:•]{1,40}):\s+(.+)$/;
const ICON_FOR: Record<string, string> = {
  hold: 'lock', confirmation: 'check', advance: 'receipt', payment: 'receipt', reminder: 'clock', prearrival: 'bed',
  event: 'cake', deposit: 'shield', refund: 'shield', thanks: 'message', cancel: 'x', quotation: 'rupee',
};

/**
 * Turns the message text (as filled from the booking, or as edited) into blocks: "Label: value" lines become
 * rows, "• " lines become a list, everything else stays as text. The greeting and the "— Nandanam Farms"
 * sign-off are dropped because the picture has its own.
 */
export function messageBlocks(text: string): { intro: string; blocks: Block[] } {
  const paras = text.replace(/\r/g, '').split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
  if (paras.length && /^—\s/.test(paras[paras.length - 1]!)) paras.pop();
  if (paras.length) paras[0] = paras[0]!.replace(/^(Namaste|Hi|Hello|Dear)\b[^,\n]*,\s*/i, '');
  const blocks: Block[] = [];
  for (const para of paras) {
    for (const line of para.split('\n').map(l => l.trim()).filter(Boolean)) {
      const last = blocks[blocks.length - 1], kv = KV.exec(line);
      if (line.startsWith('•')) { const item = line.replace(/^•\s*/, ''); if (last?.kind === 'bullets') last.items.push(item); else blocks.push({ kind: 'bullets', items: [item] }); }
      else if (kv && !/[.!?]$/.test(line)) { const row: [string, string] = [kv[1]!.trim(), kv[2]!.trim()]; if (last?.kind === 'kv') last.rows.push(row); else blocks.push({ kind: 'kv', rows: [row] }); }
      else if (last?.kind === 'text' && !line.endsWith(':') && para.includes(last.lines[last.lines.length - 1]!)) last.lines.push(line);
      else blocks.push({ kind: 'text', lines: [line] });
    }
  }
  const first = blocks[0];
  const raw = first?.kind === 'text' ? (blocks.shift() as { lines: string[] }).lines.join(' ') : '';
  return { intro: raw.charAt(0).toUpperCase() + raw.slice(1), blocks };
}

export function messageData(key: string, label: string, text: string, c: Customer, s: Settings, number: string, today: string): MessageData {
  const P = s.property, { intro, blocks } = messageBlocks(text);
  return {
    label, key, ref: `${number} · ${fDate(today, { dow: false, year: true })}`, greeting: `Namaste ${firstName(c)} Garu,`, intro, blocks,
    team: `Team ${P.name}`, city: P.city, phone: P.phone, tagline: 'Your perfect venue for memorable celebrations',
  };
}

const STRONG = /^(Total|Balance|Balance payable|Refund|Received|Booking ID)$/i;

export async function drawMessage(m: MessageData): Promise<HTMLCanvasElement> {
  await Promise.all([`20px ${HEAD}`, `400 14px ${BODY}`, `600 14px ${BODY}`, `700 14px ${BODY}`].map(f => document.fonts.load(f).catch(() => null)));
  const logo = await loadImage('assets/logo-sketch.png');
  const p = painter(logo);
  const { x, font, text, wrap, card, header } = p;
  const CL = PADX + 18, CW = W - 2 * PADX - 36;
  let y = p.brand(m.label.toUpperCase(), m.ref, m.tagline) + 22;

  /* Greeting and the opening sentence */
  font(24, 400, true); text(m.greeting, PADX + 6, y + 4, 29, T.ink);
  y += 4 + 29;
  if (m.intro) { font(14.5); const ls = wrap(m.intro, W - 2 * PADX - 12); ls.forEach((s, i) => text(s, PADX + 6, y + 8 + i * 23.2, 23.2, T.body)); y += 8 + ls.length * 23.2; }
  y += 2 + 14;

  if (m.blocks.length) {
    // Measure every block first so the card is drawn once at the right height.
    type Laid = { h: number; draw: (t: number) => void };
    const laid: Laid[] = m.blocks.map((b): Laid => {
      if (b.kind === 'text') {
        font(14.5); const ls = b.lines.flatMap(l => wrap(l, CW));
        return { h: ls.length * 23.2, draw: t => { font(14.5); ls.forEach((s, i) => text(s, CL, t + i * 23.2, 23.2, '#3b4a4a')); } };
      }
      if (b.kind === 'bullets') {
        font(14); const items = b.items.map(it => wrap(it, CW - 22));
        const h = items.reduce((a, ls) => a + ls.length * 21 + 6, 0);
        return { h, draw: t => {
          font(14); let tt = t;
          items.forEach(ls => { x.fillStyle = '#87CEEB'; x.beginPath(); x.arc(CL + 5, tt + 10.5, 3.5, 0, Math.PI * 2); x.fill(); ls.forEach((s, i) => text(s, CL + 18, tt + i * 21, 21, T.ink)); tt += ls.length * 21 + 6; });
        } };
      }
      // Label left, value right; a value too long for one line goes under its label and wraps.
      const rows = b.rows.map(([k, v]) => {
        font(14.5); const kw = x.measureText(k).width; font(14.5, 700); const vw = x.measureText(v).width;
        const inline = kw + vw + 24 <= CW;
        const vl = inline ? [v] : (font(14, 600), wrap(v, CW));
        return { k, v, inline, vl, h: inline ? 44 : 12 + 20 + vl.length * 20 + 10 };
      });
      return { h: rows.reduce((a, r) => a + r.h, 0), draw: t => {
        let tt = t;
        rows.forEach((r, i) => {
          const strong = STRONG.test(r.k), neg = r.v.startsWith('−');
          if (r.inline) {
            font(14.5); text(r.k, CL + 2, tt, r.h, T.muted);
            font(strong ? 16 : 14.5, 700); text(r.v, CL + CW - 2, tt, r.h, neg ? T.red : strong ? T.slate : T.ink, 'right');
          } else {
            font(14.5); text(r.k, CL + 2, tt + 12, 20, T.muted);
            font(14, 600); r.vl.forEach((s, j) => text(s, CL + 2, tt + 32 + j * 20, 20, T.ink));
          }
          if (i < rows.length - 1) { x.fillStyle = T.divider; x.fillRect(CL, tt + r.h - 1, CW, 1); }
          tt += r.h;
        });
      } };
    });
    const GAP = 12;
    const inner = laid.reduce((a, l) => a + l.h, 0) + GAP * (laid.length - 1);
    const h = 16 + 34 + 12 + inner + 18;
    card(y, h);
    header(CL, y + 16, ICON_FOR[m.key] ?? 'message', m.label);
    let t = y + 16 + 34 + 12;
    for (const l of laid) { l.draw(t); t += l.h + GAP; }
    y += h + 14;
  }
  return p.finish(p.footer(y, m.team, m.city, m.phone));
}

async function toFile(cv: HTMLCanvasElement, name: string): Promise<File> {
  const blob = await new Promise<Blob>((res, rej) => cv.toBlob(b => (b ? res(b) : rej(new Error('Could not make the picture'))), 'image/png'));
  return new File([blob], name, { type: 'image/png' });
}

export async function messageFile(m: MessageData, number: string): Promise<File> {
  return toFile(await drawMessage(m), `Nandanam-${m.key}-${number}.png`);
}

export async function quoteFile(r: BookingRecord, c: Customer, s: Settings, number: string, today: string): Promise<File> {
  return toFile(await drawQuote(quoteData(r, c, s, number, today)), `Nandanam-quotation-${number}.png`);
}

/** Share sheet where the phone has one (pick WhatsApp there); otherwise the picture is downloaded. */
export async function sharePicture(f: File): Promise<'shared' | 'saved' | 'cancelled'> {
  if (navigator.canShare?.({ files: [f] })) {
    try { await navigator.share({ files: [f] }); return 'shared'; }
    catch (e) { if (e instanceof DOMException && e.name === 'AbortError') return 'cancelled'; }
  }
  const a = document.createElement('a'); a.href = URL.createObjectURL(f); a.download = f.name; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
  return 'saved';
}
